import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Paper,
  IconButton,
  Tooltip,
  Fab,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  OpenInNew as OpenInNewIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useChatPanel } from '../contexts/ChatPanelContext';
import ChatInterface from './ChatInterface';
import useChat from '../hooks/useChat';
import { keyframes } from '@mui/system';
import SparkleIcon from './SparkleIcon';

// Animation for the minimized state pulse effect
const pulse = keyframes`
  0% { opacity: 0.6; transform: scale(1); }
  50% { opacity: 1; transform: scale(1.1); }
  100% { opacity: 0.6; transform: scale(1); }
`;

// Animation for the AI icon rotation on hover - single rotation
const rotateOnce = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

/**
 * IMPROVED: Simplified ChatPanel without dual state management
 * All conversation state managed by ChatPanelContext via useChat hook
 */
const ChatPanel = ({ onActionExecute }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const responsive = theme.modern?.responsive || {};
  const chatPanelSizing = responsive.chatPanel || {};
  const floatingFabSize = chatPanelSizing.floatingFabSize || { xs: 58, sm: 56 };
  const floatingFabIconSize = chatPanelSizing.floatingFabIconSize || { xs: 26, sm: 24 };
  const panelTop = chatPanelSizing.panelTop || { xs: 74, sm: 70 };
  const panelBottom = chatPanelSizing.panelBottom || { xs: 10, sm: 6 };
  const panelHorizontalInset = chatPanelSizing.panelHorizontalInset || { xs: 8, sm: 0 };
  const panelWidth = chatPanelSizing.panelWidth || { xs: 'calc(100vw - 16px)', sm: '100%' };
  const panelMaxWidth = chatPanelSizing.panelMaxWidth || { xs: 'calc(100vw - 16px)', sm: '100%' };
  const panelShadow = chatPanelSizing.panelShadow || { xs: '0 0 24px rgba(0,0,0,0.18)', sm: '2px 0 8px rgba(0,0,0,0.1)' };
  const panelRadius = chatPanelSizing.panelRadius || { xs: '16px', sm: '0 20px 20px 0' };
  const headerPadding = chatPanelSizing.headerPadding || { xs: 1, sm: 0.5 };
  const headerBorderBottom = chatPanelSizing.headerBorderBottom || { xs: '1px solid #f1f1f1', sm: '0px solid #f1f1f1' };
  const headerMinHeight = chatPanelSizing.headerMinHeight || { xs: 52, sm: 39 };
  const headerActionsGap = chatPanelSizing.headerActionsGap || { xs: 1, sm: 0.5 };
  const actionButtonSize = chatPanelSizing.actionButtonSize || { xs: 40, sm: 32 };
  const actionIconSize = chatPanelSizing.actionIconSize || { xs: 22, sm: 18 };
  const minimizedActionButtonSize = chatPanelSizing.minimizedActionButtonSize || { xs: 40, sm: 28 };
  const minimizedActionIconSize = chatPanelSizing.minimizedActionIconSize || { xs: 22, sm: 16 };
  const floatingFabIconPixelSize = typeof floatingFabIconSize === 'number'
    ? floatingFabIconSize
    : floatingFabIconSize?.xs || 26;
  const mobileFloatingSparkleSize = Math.max(Math.round(floatingFabIconPixelSize * 1.4), 34);

  const {
    chatPanelState,
    hideChatPanel,
    toggleMinimizeChat,
    returnToChat,
    resizeChatPanel,
  } = useChatPanel();

  // Use the chat hook - conversation comes directly from context
  const {
    query,
    setQuery,
    conversation, // Direct reference to context conversation
    loading,
    messagesEndRef,
    handleSendMessage,
    handleMessageFeedback,
    feedbackSubmittingId,
    tokenLimitReached,
  } = useChat({
    onActionExecute
  });

  // Extract AI thinking state from chat panel state
  const isAiThinking = chatPanelState.isAiThinking;

  // Panel-specific state
  const panelRef = useRef(null);
  const resizeRef = useRef(null);
  const [isResizing, setIsResizing] = useState(false);
  const [isPanelHovered, setIsPanelHovered] = useState(false); // State for whole panel hover
  const [sparkleRotateKey, setSparkleRotateKey] = useState(0); // Key to trigger single rotation
  const collapseActionButtonSize = chatPanelState.isMinimized ? minimizedActionButtonSize : actionButtonSize;
  const collapseActionIconSize = chatPanelState.isMinimized ? minimizedActionIconSize : actionIconSize;

  // Handle resize functionality
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || isMobile) return;
      
      const panelRect = panelRef.current?.getBoundingClientRect();
      if (panelRect) {
        const newWidth = e.clientX - panelRect.left;
        resizeChatPanel(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing && !isMobile) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isMobile, isResizing, resizeChatPanel]);

  // Keep the latest messages visible when reopening/expanding the panel.
  useEffect(() => {
    if (!chatPanelState.isVisible || chatPanelState.isMinimized) {
      return;
    }

    const scrollToBottom = () => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'auto', block: 'end' });
    };

    // Wait for the layout to settle after display changes.
    const timeoutId = setTimeout(() => {
      scrollToBottom();
      requestAnimationFrame(scrollToBottom);
    }, 70);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [chatPanelState.isVisible, chatPanelState.isMinimized, messagesEndRef]);

  const handleMouseDown = (e) => {
    if (isMobile) {
      return;
    }

    e.preventDefault();
    setIsResizing(true);
  };

  if (!chatPanelState.isVisible) return null;

  if (isMobile && chatPanelState.isMinimized) {
    return (
      <Tooltip title="Open AI chat" placement="right">
        <Fab
          disableRipple
          onClick={toggleMinimizeChat}
          aria-label="Open AI chat"
          sx={{
            position: 'fixed',
            left: 4,
            bottom: 4,
            zIndex: 1300,
            width: floatingFabSize,
            height: floatingFabSize,
            // backgroundColor: 'transparent',
            boxShadow: 'none',
            borderRadius: '50%',
            background: 'linear-gradient(145deg, rgba(255,255,255,0.38) 0%, rgba(255,255,255,0.14) 100%)',
            border: '1px solid rgba(255,255,255,0.42)',
            backdropFilter: 'blur(6px) saturate(170%)',
            WebkitBackdropFilter: 'blur(6px) saturate(170%)',
            boxShadow: '0 8px 20px rgba(15, 23, 42, 0.26), inset 0 1px 1px rgba(255,255,255,0.55)',
            '&:hover': {
              backgroundColor: 'transparent',
              boxShadow: 'none',
            },
            '&.Mui-focusVisible': {
              backgroundColor: 'transparent',
            }
          }}
        >
          <SparkleIcon
            size={mobileFloatingSparkleSize}
            animate="none"
            glass
            colors={{
              start: '#7c3aed',
              middle: '#a855f7',
              end: '#db2777'
            }}
          />
        </Fab>
      </Tooltip>
    );
  }

  return (
    <Paper
      ref={panelRef}
      elevation={0}
      onClick={!isMobile && chatPanelState.isMinimized ? toggleMinimizeChat : undefined} // Make entire panel clickable when minimized
      onMouseEnter={() => {
        if (!isMobile && chatPanelState.isMinimized) {
          setIsPanelHovered(true);
          setSparkleRotateKey(prev => prev + 1); // Trigger single rotation
        }
      }}
      onMouseLeave={() => {
        if (!isMobile && chatPanelState.isMinimized) {
          setIsPanelHovered(false);
        }
      }}
      sx={{
        position: 'fixed',
        top: panelTop,
        left: chatPanelState.isMinimized
          ? { xs: panelHorizontalInset.xs, sm: `-${chatPanelState.width - 35}px` }
          : panelHorizontalInset,
        right: 'auto',
        bottom: panelBottom,
        width: { xs: panelWidth.xs, sm: chatPanelState.width },
        maxWidth: { xs: panelMaxWidth.xs, sm: chatPanelState.width },
        zIndex: { xs: 1300, sm: 2 },
        display: 'flex',
        flexDirection: 'column',
        borderRadius: panelRadius,
        borderRight: { xs: '1px solid #e0e0e0', sm: '1px solid #e0e0e0' },
        borderTop: { xs: '1px solid #e0e0e0', sm: 'none' },
        backgroundColor: '#ffffff',
        boxShadow: panelShadow,
        transition: 'all 0.3s ease-in-out', // Smooth slide animation with hover effects
        cursor: chatPanelState.isMinimized ? { xs: 'default', sm: 'pointer' } : 'default',
        ...(chatPanelState.isMinimized && {
          '&:hover': {
            left: { xs: panelHorizontalInset.xs, sm: `-${chatPanelState.width -40}px` }, // Move out slightly more on hover (show 10px more)
            boxShadow: '4px 0 4px rgba(0,0,0,0.15)',
          }
        })
      }}
    >
      {/* Resize Handle */}
      {!isMobile && !chatPanelState.isMinimized && (
        <Box
          ref={resizeRef}
          onMouseDown={handleMouseDown}
          sx={{
            position: 'absolute',
            right: 0,
            top: 0,
            bottom: 0,
            width: 3,
            cursor: 'col-resize',
            backgroundColor: 'transparent',
            '&:hover': {
              // backgroundColor: '#3B7B86',
            },
            zIndex: 1,
          }}
        />
      )}

      {/* Bottom Right Icon for Collapsed State */}
      {!isMobile && chatPanelState.isMinimized && (
        <Tooltip title="Expand chat panel" placement="left">
          <Box
            sx={{
              position: 'absolute',
              bottom: 16,
              right: 8,
              cursor: 'pointer',
              zIndex: 3,
              pointerEvents: 'none', // Disable pointer events so hover goes to parent
            }}
          >
            <SparkleIcon
              key={sparkleRotateKey} // Use key to trigger re-render and animation
              className="sparkle-icon"
              size={24}
              animate="none" // No default animation
              // glass
              colors={{
                start: '#7c3aed',
                middle: '#a855f7',
                end: '#db2777'
              }}
              sx={{
                animation: isPanelHovered ? `${rotateOnce} 0.6s ease-in-out` : 'none',
              }}
            />
          </Box>
        </Tooltip>
      )}

      {/* Chat Header - Only icons, no title */}
      <Box
        sx={{
          p: headerPadding,
          borderBottom: headerBorderBottom,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
          backgroundColor: 'transparent',
          minHeight: headerMinHeight,
        }}
      >
        <Box sx={{ display: 'flex', gap: headerActionsGap }}>
          <Tooltip title="Open in full chat page">
            <IconButton
              size={isMobile ? 'medium' : 'small'}
              onClick={returnToChat}
              sx={{ 
                color: '#666',
                '&:hover': { 
                  color: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)' 
                },
                width: actionButtonSize,
                height: actionButtonSize,
              }}
            >
              <OpenInNewIcon sx={{ fontSize: actionIconSize }} />
            </IconButton>
          </Tooltip>
          
          <Tooltip title={chatPanelState.isMinimized ? "Restore panel" : isMobile ? "Minimize to floating button" : "Hide panel"}>
            <IconButton
              size={isMobile ? 'medium' : 'small'}
              onClick={(e) => {
                e.stopPropagation(); // Prevent parent click handler
                toggleMinimizeChat();
              }}
              sx={{ 
                color: '#666',
                '&:hover': { 
                  color: '#1976d2',
                  backgroundColor: 'rgba(25, 118, 210, 0.1)' 
                },
                width: collapseActionButtonSize,
                height: collapseActionButtonSize,
                zIndex: 6, // Above everything else
              }}
            >
              {chatPanelState.isMinimized ? (
                <ChevronRightIcon sx={{ fontSize: collapseActionIconSize }} />
              ) : (
                isMobile ? (
                  <CloseIcon sx={{ fontSize: actionIconSize }} />
                ) : (
                  <ChevronLeftIcon sx={{ fontSize: actionIconSize }} />
                )
              )}
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      {/* Chat Content */}
      <Box
        sx={{
          flex: 1,
          display: chatPanelState.isMinimized ? 'none' : 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          height: '100%'
        }}
      >
        {/* Scrollable messages area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            minHeight: 0, // Important for flex scrolling
          }}
        >
          <ChatInterface
            conversation={conversation}
            query={query}
            setQuery={setQuery}
            loading={loading}
            isAiThinking={isAiThinking}
            onSendMessage={handleSendMessage}
            onSubmitFeedback={handleMessageFeedback}
            feedbackSubmittingId={feedbackSubmittingId}
            messagesEndRef={messagesEndRef}
            variant="panel"
            placeholder="What would you like to know?"
            showLoadingIndicator={false} // We handle loading in the conversation
            tokenLimitReached={tokenLimitReached}
          />
        </Box>
      </Box>
    </Paper>
  );
};

export default ChatPanel;
